import React, { useState } from "react";
import { GENERAL_URL_API, store } from "./components/Store";
import { useContext, useEffect } from "react";
import { SesionAPI } from "./services/api/Sesion";
import { useHistory, useLocation } from "react-router-dom";
import { CancelRequestController } from "./util/cancel-request-controller";
import { ClearStorage } from "./util/clear-storage";
import getAjax from "./js/utilities/getAjax";
import { PersonalAccountAPI } from "./services/api/personal-account";
import {
	useAccountFavoritesDataLoader,
	useStorageFavoritesDataLoader,
} from "./hooks/data-loader/use-favorites-data-loader";
import { useUserBooks } from "./hooks/useUserBooks";
import { buildMakeMakeComercialUrlByEnv } from "./util/host-utilities";

/**
 * Componente que actúa como wrapper el cual verifica el estado de la sesión del usuario y manipula el dom dependiendo del estado, este componente es importante para la seguridad de los datos en el frontend.
 * @param {object} children
 * @returns {JSX.Element} VerificarSesion
 */
export const VerificarSesion = ({ children }) => {
	const location = useLocation();
	const history = useHistory();
	const { state, dispatch } = useContext(store);
	const [validationFinished, setValidationFinished] = useState(false);
	const accountFavoritesLoader = useAccountFavoritesDataLoader();
	const storageFavoritesLoader = useStorageFavoritesDataLoader();
	const userBooks = useUserBooks();
	const isAuth = state.sesionActiva === "SI";

	const loadUserData = async ({ isAuth }) => {
		await userBooks.load();
		getAjax(
			`${GENERAL_URL_API}/assets/datosHeader`,
			{ sesion: isAuth ? "SI" : "NO" },
			async (response) => {
				const res = response !== "Error" ? JSON.parse(response) : {};
				let makemakeData = res.makemakeData || localStorage.getItem("makemakeData") || {};
				let categoriasMenu =
					res.categoriasMenu || localStorage.getItem("categoriasMenu") || {};
				window.localStorage.setItem("makemakeData", JSON.stringify(makemakeData));
				window.localStorage.setItem("categoriasMenu", JSON.stringify(categoriasMenu));
				const isPersonalAccount = makemakeData?.cuentaPersonal === "1";
				const shouldCreatePersonalAccounts = ["si", "Si", "SI", true, "1"].includes(
					makemakeData?.datosHeader?.permitirIndividuales
				);
				const isAlternativeFlow =["si", "Si", "SI", true, "1"].includes(
					makemakeData?.datosHeader?.flujoAlterno
				);
				dispatch({
					type: "CHANGE_VALUE",
					property: "pais",
					value: makemakeData.pais,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "isPersonalAccount",
					value: isPersonalAccount,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "shouldCreatePersonalAccounts",
					value: shouldCreatePersonalAccounts && !isPersonalAccount,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "shouldAssociatePersonalAccountByAlternativeFlow",
					value: isAlternativeFlow && !isPersonalAccount && shouldCreatePersonalAccounts,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "sesionActiva",
					value: makemakeData.sesionActiva,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "userType",
					value: makemakeData.userType,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "username",
					value: makemakeData.username,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "datosHeader",
					value: makemakeData.datosHeader,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "categoriasMenu",
					value: categoriasMenu,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "tipo",
					value: makemakeData?.tipo,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "adminSuperInstituto",
					value: makemakeData?.adminSuperInstituto,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "linkAcceso",
					value: makemakeData?.linkAcceso,
				});
				dispatch({
					type: "CHANGE_VALUE",
					property: "isAuthByIP",
					value: makemakeData?.ingresoPorIp === 'SI',
				});
				if (isPersonalAccount) {
					await loadPersonalAccountData();
				}
				dispatch({ type: "CHANGE_VALUE", property: "mainDataLoaded", value: true });
			}
		);
	};


	const loadPersonalAccountData = async () => {
		await PersonalAccountAPI.getUserProfileData({ abortSignal: null })
			.then((res) => {
				dispatch({
					type: "CHANGE_VALUE",
					property: "personalAccount",
					value: res,
				});
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const verificar = async ({ abortSignal }) => {
		await SesionAPI.usuarioAutenticado({ abortSignal })
			.then((res) => {
				const redirectParam = new URLSearchParams(location.search).get("redirect");
				if(location.pathname === "/" && redirectParam) {
					// no hacer nada, application redirecciona.
				}
				else if(location.pathname === "/" && res === false) {
					window.location.replace(buildMakeMakeComercialUrlByEnv({path: "/"}));
				}
				else if (location.pathname === "/" && res === true) {
					history.push("/home");
				}
				else if (location.pathname === "/comunidad" && res === false) {
					const sectionParamValues = {
						"makemake-en-vivo": "makemake-en-vivo",
						"planes-de-lectura": "planes-de-lectura",
					}
					const paramsToHash = (key)=>{
						const hash = sectionParamValues[key];
						if(!hash) return '';
						return `#${hash}`
					}
					const queryParams = new URLSearchParams(location.search);
					const section = queryParams.get("section");
					const hash = paramsToHash(section);
					const redirectUrl = buildMakeMakeComercialUrlByEnv({path: "/comunidad", hash})
					window.location.replace(redirectUrl);
				}
				else if (location.pathname === "/login" && res === true) {
					window.location.replace("/home");
				}
				if (state.mainDataLoaded !== true) loadUserData({ isAuth: res });
				if (res) {
					if (window.BroadcastChannel) {
						const bc = new BroadcastChannel("isAuth");
						bc.postMessage(
							{
								eventName: "isAuth",
								type: "internalAuthVerification",
								payload: true,
							},
							window.location.origin
						);
					}
				}
				if (res === false && state.sesionActiva === "SI") {
					loadUserData();
					ClearStorage();
					window.location.reload();
				}
			})
			.catch((err) => err)
			.finally(() => {
				setValidationFinished(true);
			});
	};

	useEffect(() => {
		const abortController = new CancelRequestController();
		verificar({ abortSignal: abortController.signal });
		return () => {
			abortController.abort();
		};
	}, [state.sesionActiva, location.pathname, state.mainDataLoaded]);

	useEffect(() => {
		const abortController = new CancelRequestController();
		const loadFavorites = async()=>{
			if(!isAuth) return;
			if(state.mainDataLoaded && userBooks.loadCompleted){
				if(state.isPersonalAccount){
					accountFavoritesLoader.load({ abortSignal: abortController.signal });
				}else{
					storageFavoritesLoader.load();
				}
			}
		}
		loadFavorites();
		return () => {
			abortController.abort();
		};
		
	}, [isAuth, state.mainDataLoaded, userBooks.loadCompleted, state.isPersonalAccount]);

	// Importante mantener este conditional rendering.
	return <>{validationFinished ? children : <></>}</>;
};
